<template>
    <div class="pl-page inv-page">

        <!-- SEARCH TOOLBAR -->
        <div class="compact-toolbar inv-toolbar">
            <div class="toolbar-group">
                <span class="inv-toolbar-label">
                    <span class="pc-ico"><b-icon icon="search"></b-icon></span>
                    Lookup Inventory
                </span>
            </div>
            <div class="toolbar-group inv-search-group">
                <b-form-input
                    size="sm"
                    v-model="product_query"
                    @keyup.enter="fetch_data"
                    placeholder="Enter SKU or product name…"
                    class="inv-search-input"
                ></b-form-input>
                <b-button size="sm" class="inv-search-btn" @click="fetch_data" :disabled="items_being_loaded">
                    <b-spinner v-if="items_being_loaded" small></b-spinner>
                    <span v-else>SEARCH</span>
                </b-button>
            </div>
        </div>

        <!-- LOADING / EMPTY -->
        <div v-if="items_being_loaded" class="inv-empty">
            <b-spinner variant="primary" label="Loading"></b-spinner>
            <p>Loading inventory…</p>
        </div>
        <div v-else-if="items.length === 0" class="inv-empty">
            <span class="pc-ico inv-empty-ico"><b-icon icon="box"></b-icon></span>
            <p>{{ message }}</p>
        </div>

        <!-- RESULTS -->
        <div v-else class="pc-list inv-list">
            <!-- Sort bar -->
            <div class="pc-bar">
                <div class="pc-bar-sort">
                    <span class="pc-bar-label"><span class="pc-ico"><b-icon icon="arrow-down-up"></b-icon></span> Sort by</span>
                    <select class="pc-bar-select" :value="activeSortKey || ''" @change="onSortSelect($event.target.value)">
                        <option value="" disabled>Choose a field</option>
                        <option v-for="opt in sortOptions" :key="opt.key" :value="opt.key">{{ opt.label }}</option>
                    </select>
                    <button v-if="activeSortKey" type="button" class="pc-bar-btn" @click="sortby(activeSortKey)">
                        <span class="pc-ico"><b-icon :icon="activeSortAsc ? 'arrow-up' : 'arrow-down'"></b-icon></span>
                        {{ activeSortAsc ? 'Ascending' : 'Descending' }}
                    </button>
                </div>
                <div class="pc-bar-view">
                    <span class="pc-bar-count">
                        {{ groupedItems.length }} card{{ groupedItems.length === 1 ? '' : 's' }}
                        · {{ items.length }} row{{ items.length === 1 ? '' : 's' }}
                        · {{ totalUnits }} units
                    </span>
                    <button type="button" class="pc-bar-btn" @click="toggleExpandAll">
                        <span class="pc-ico"><b-icon :icon="allExpanded ? 'arrows-collapse' : 'arrows-expand'"></b-icon></span>
                        {{ allExpanded ? 'Collapse all' : 'Expand all' }}
                    </button>
                </div>
            </div>

            <!-- Column header -->
            <div class="pc-head inv-head">
                <div class="pc-head-cell"></div>
                <div class="pc-head-cell">
                    <button type="button" :class="pcSortClass('product_name')" @click="sortby('product_name')">
                        Product<b-icon v-if="activeSortKey === 'product_name'" :icon="activeSortAsc ? 'arrow-up' : 'arrow-down'"></b-icon>
                    </button>
                </div>
                <div class="pc-head-cell">
                    <button type="button" :class="pcSortClass('locsub')" @click="sortby('locsub')">
                        Location<b-icon v-if="activeSortKey === 'locsub'" :icon="activeSortAsc ? 'arrow-up' : 'arrow-down'"></b-icon>
                    </button>
                </div>
                <div class="pc-head-cell is-num">
                    <button type="button" :class="pcSortClass('nitems')" @click="sortby('nitems')">
                        Qty<b-icon v-if="activeSortKey === 'nitems'" :icon="activeSortAsc ? 'arrow-up' : 'arrow-down'"></b-icon>
                    </button>
                </div>
                <div class="pc-head-cell is-num">
                    <button type="button" :class="pcSortClass('reserved')" @click="sortby('reserved')">
                        Reserved<b-icon v-if="activeSortKey === 'reserved'" :icon="activeSortAsc ? 'arrow-up' : 'arrow-down'"></b-icon>
                    </button>
                </div>
                <div class="pc-head-cell is-num">
                    <button type="button" :class="pcSortClass('available')" @click="sortby('available')">
                        Available<b-icon v-if="activeSortKey === 'available'" :icon="activeSortAsc ? 'arrow-up' : 'arrow-down'"></b-icon>
                    </button>
                </div>
                <div class="pc-head-cell is-num">
                    <button type="button" :class="pcSortClass('missing')" @click="sortby('missing')">
                        Missing<b-icon v-if="activeSortKey === 'missing'" :icon="activeSortAsc ? 'arrow-up' : 'arrow-down'"></b-icon>
                    </button>
                </div>
                <div class="pc-head-cell is-num">
                    <button type="button" :class="pcSortClass('received')" @click="sortby('received')">
                        Received<b-icon v-if="activeSortKey === 'received'" :icon="activeSortAsc ? 'arrow-up' : 'arrow-down'"></b-icon>
                    </button>
                </div>
                <div class="pc-head-cell"></div>
            </div>

            <!-- Totals -->
            <div class="inv-totals">
                <div class="pc-head-cell"></div>
                <div class="pc-head-cell"></div>
                <div class="pc-head-cell"></div>
                <div class="pc-head-cell is-num"><span class="inv-total-val">{{ total('nitems') }}</span></div>
                <div class="pc-head-cell is-num"><span class="inv-total-val">{{ total('reserved') }}</span></div>
                <div class="pc-head-cell is-num"><span class="inv-total-val is-good">{{ total('available') }}</span></div>
                <div class="pc-head-cell is-num"><span class="inv-total-val is-bad">{{ total('missing') }}</span></div>
                <div class="pc-head-cell is-num"><span class="inv-total-val">{{ total('received') }}</span></div>
                <div class="pc-head-cell"></div>
            </div>

            <!-- ONE CARD PER (SKU + LOCATION) -->
            <article
                v-for="loc in groupedItems"
                :key="loc.key"
                class="pc-card inv-card inv-location-card"
                :class="['is-' + loc.stockState, { 'is-open': loc.expanded }]"
            >
                <!-- LOCATION HEADER -->
                <div class="pc-sum inv-sum">
                    <div class="inv-col inv-col-img">
                        <button
                            v-if="productImage(loc.representative) && !loc.representative._imgError"
                            type="button"
                            class="inv-img-btn"
                            @click="showFullImage(loc.representative)"
                            v-b-tooltip.hover.top="'View image'"
                        >
                            <img
                                :src="productImage(loc.representative)"
                                :alt="loc.representative.product_sku"
                                @error="$set(loc.representative, '_imgError', true)"
                            />
                        </button>
                        <div v-else class="inv-img-empty">
                            <span class="pc-ico"><b-icon icon="image"></b-icon></span>
                        </div>
                    </div>

                    <div class="inv-col inv-col-product">
                        <div class="pc-name inv-name" :title="loc.representative.product_name">
                            {{ trimName(loc.representative.product_name) }}
                        </div>
                        <div class="inv-sku-row">
                            <span class="inv-sku-label">SKU</span>
                            <span class="inv-sku">{{ loc.representative.product_sku }}</span>
                        </div>
                        <div class="pc-badges inv-badges">
                            <span v-if="loc.stockState === 'out'" class="pc-badge is-danger">Out of stock</span>
                            <span v-if="loc.stockState === 'low'" class="pc-badge is-warn">Low stock</span>
                            <span v-if="loc.missing > 0" class="pc-badge is-muted">Has missing</span>
                            <span class="pc-badge is-info">{{ loc.sublocations.length }} sublocation(s)</span>
                        </div>
                    </div>

                    <div class="inv-col inv-col-loc">
                        <div class="inv-loc-name">{{ loc.locationName }}</div>
                        <div class="inv-loc-meta">
                            <span class="pc-ico"><b-icon icon="geo-alt"></b-icon></span>
                            Location {{ loc.location_id }}
                        </div>
                    </div>

                    <div class="inv-col inv-col-num"><span class="inv-v">{{ loc.nitems }}</span></div>
                    <div class="inv-col inv-col-num"><span class="inv-v is-reserved">{{ loc.reserved }}</span></div>
                    <div class="inv-col inv-col-num"><span class="inv-v is-good">{{ loc.available }}</span></div>
                    <div class="inv-col inv-col-num">
                        <span class="inv-v" :class="loc.missing > 0 ? 'is-bad' : 'is-muted'">{{ loc.missing }}</span>
                    </div>
                    <div class="inv-col inv-col-num">
                        <span class="inv-v is-received">{{ loc.received }}</span>
                    </div>

                    <div class="inv-col inv-col-actions">
                        <button
                            type="button"
                            class="pc-icon-btn inv-expand-btn"
                            @click="toggleLocation(loc)"
                            v-b-tooltip.hover.top="loc.expanded ? 'Hide sublocations' : 'Show sublocations'"
                        >
                            <span class="pc-ico"><b-icon :icon="loc.expanded ? 'chevron-up' : 'chevron-down'"></b-icon></span>
                        </button>
                    </div>
                </div>

                <!-- SUBLOCATIONS -->
                <div v-if="loc.expanded" class="inv-sublocations">
                    <div class="inv-subloc-head-row">
                        <div class="inv-sub-col inv-sub-col-name">Sublocation</div>
                        <div class="inv-sub-col inv-sub-col-qty is-num">Qty</div>
                        <div class="inv-sub-col inv-sub-col-num is-num">Reserved</div>
                        <div class="inv-sub-col inv-sub-col-num is-num">Available</div>
                        <div class="inv-sub-col inv-sub-col-num is-num">Missing</div>
                        <div class="inv-sub-col inv-sub-col-num is-num">Received</div>
                        <div class="inv-sub-col inv-sub-col-actions"></div>
                    </div>

                    <div
                        v-for="sub in loc.sublocations"
                        :key="sub.key"
                        class="inv-subloc-row"
                        :class="['is-' + sub.stockState, { 'is-editing': isEditing(sub.item) }]"
                    >
                        <div class="inv-sub-col inv-sub-col-name">
                            <div class="inv-subloc-title">{{ sub.sublocationLabel }}</div>
                            <div class="inv-subloc-meta">
                                <span class="inv-subloc-chip">{{ sub.locsub }}</span>
                                <span v-if="sub.is_out_of_stock" class="pc-badge is-danger">Out of stock loc</span>
                                <span v-if="sub.missing > 0" class="pc-badge is-muted">Has missing</span>
                            </div>
                        </div>

                        <div class="inv-sub-col inv-sub-col-qty is-num">
                            <b-input
                                size="sm"
                                class="pc-input inv-qty-input"
                                v-model="sub.item.nitems"
                                :disabled="sub.item.is_out_of_stock || !isAdmin"
                                type="number"
                                min="0"
                                @focus="onQtyFocus(sub.item)"
                                @blur="onQtyBlur(sub.item)"
                            ></b-input>
                        </div>

                        <div class="inv-sub-col inv-sub-col-num is-num">
                            <span class="inv-v is-reserved">{{ sub.reserved }}</span>
                        </div>
                        <div class="inv-sub-col inv-sub-col-num is-num">
                            <span class="inv-v is-good">{{ sub.available }}</span>
                        </div>
                        <div class="inv-sub-col inv-sub-col-num is-num">
                            <span class="inv-v" :class="sub.missing > 0 ? 'is-bad' : 'is-muted'">{{ sub.missing }}</span>
                        </div>
                        <div class="inv-sub-col inv-sub-col-num is-num">
                            <span class="inv-v is-received">{{ sub.received }}</span>
                        </div>

                        <div class="inv-sub-col inv-sub-col-actions">
                            <button
                                type="button"
                                class="pc-btn is-primary"
                                :disabled="sub.item.is_out_of_stock || !isAdmin"
                                @click="update_inventory(sub.item)"
                                v-b-tooltip.hover.top="'Update quantity'"
                            >
                                <span class="pc-ico"><b-icon icon="check2"></b-icon></span> UPDATE
                            </button>
                            <button
                                type="button"
                                class="pc-icon-btn"
                                @click="show_history(sub.item)"
                                v-b-tooltip.hover.top="'Inventory update history'"
                            >
                                <span class="pc-ico"><b-icon icon="clock-history"></b-icon></span>
                            </button>
                            <button
                                type="button"
                                class="pc-icon-btn is-received"
                                @click="show_received_history(sub.item)"
                                v-b-tooltip.hover.top="'Received history'"
                            >
                                <span class="pc-ico"><b-icon icon="box-arrow-in-down"></b-icon></span>
                            </button>
                        </div>
                    </div>
                </div>
            </article>
        </div>

        <!-- ============================================== -->
        <!-- UPDATE HISTORY MODAL                           -->
        <!-- ============================================== -->
        <b-modal
            id="inv-history-modal"
            v-model="history_visible"
            size="lg"
            centered
            scrollable
            hide-footer
            title="Update History"
            header-class="inv-modal-header"
            body-class="inv-modal-body"
        >
            <div v-if="history_heading" class="inv-modal-summary">
                <div class="inv-modal-summary-icon is-orange">
                    <b-icon icon="clock-history"></b-icon>
                </div>
                <div class="inv-modal-summary-text">
                    <div class="inv-modal-summary-title">{{ history_heading.product_sku }}</div>
                    <div class="inv-modal-summary-sub">
                        <span class="pc-ico"><b-icon icon="geo-alt"></b-icon></span>
                        {{ history_heading.locsub }}
                    </div>
                </div>
                <div class="inv-modal-summary-count">
                    <b>{{ history.length }}</b>
                    <small>updates</small>
                </div>
            </div>

            <div v-if="history_loading" class="inv-empty">
                <b-spinner variant="primary"></b-spinner>
                <p>Loading history…</p>
            </div>
            <div v-else-if="history.length === 0" class="inv-empty">
                <span class="pc-ico inv-empty-ico"><b-icon icon="clock"></b-icon></span>
                <p>No history for this item yet.</p>
            </div>
            <div v-else class="inv-timeline">
                <div v-for="h in history" :key="h.id" class="inv-timeline-item">
                    <div class="inv-timeline-marker">
                        <b-icon :icon="h.delta < 0 ? 'arrow-down' : 'arrow-up'"></b-icon>
                    </div>
                    <div class="inv-timeline-card">
                        <div class="inv-timeline-head">
                            <span class="inv-timeline-when">{{ format_date(h.changed_at) }}</span>
                            <span class="inv-timeline-user">
                                <b-icon icon="person"></b-icon>
                                {{ h.user_name || h.user_id || '—' }}
                            </span>
                        </div>
                        <div class="inv-timeline-body">
                            <div class="inv-timeline-qty">
                                <span class="inv-qty-old">{{ h.old_nitems }}</span>
                                <span class="inv-qty-arrow">→</span>
                                <span class="inv-qty-new">{{ h.new_nitems }}</span>
                                <span class="inv-qty-delta" :class="h.delta < 0 ? 'is-neg' : 'is-pos'">
                                    {{ h.delta > 0 ? '+' : '' }}{{ h.delta }}
                                </span>
                            </div>
                            <div v-if="h.missing_delta > 0" class="inv-timeline-missing">
                                <b-icon icon="exclamation-triangle"></b-icon>
                                Moved <b>+{{ h.missing_delta }}</b> to missing
                                <span class="inv-muted">(total {{ h.missing_after }})</span>
                            </div>
                            <div v-if="h.note" class="inv-timeline-note">
                                <b-icon icon="chat-left-text"></b-icon>
                                {{ h.note }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </b-modal>

        <!-- ============================================== -->
        <!-- RECEIVED HISTORY MODAL                         -->
        <!-- ============================================== -->
        <b-modal
            id="inv-received-history-modal"
            v-model="received_visible"
            size="lg"
            centered
            scrollable
            hide-footer
            title="Received History"
            header-class="inv-modal-header inv-modal-header--received"
            body-class="inv-modal-body"
        >
            <div v-if="received_heading" class="inv-modal-summary">
                <div class="inv-modal-summary-icon is-blue">
                    <b-icon icon="box-arrow-in-down"></b-icon>
                </div>
                <div class="inv-modal-summary-text">
                    <div class="inv-modal-summary-title">{{ received_heading.product_sku }}</div>
                    <div class="inv-modal-summary-sub">
                        <span class="pc-ico"><b-icon icon="geo-alt"></b-icon></span>
                        {{ received_heading.locsub }}
                    </div>
                </div>
                <div class="inv-modal-summary-count is-blue">
                    <b>{{ received_total }}</b>
                    <small>units</small>
                </div>
            </div>

            <div v-if="received_loading" class="inv-empty">
                <b-spinner variant="primary"></b-spinner>
                <p>Loading received history…</p>
            </div>
            <div v-else-if="received_list.length === 0" class="inv-empty">
                <span class="pc-ico inv-empty-ico"><b-icon icon="box"></b-icon></span>
                <p>No receiving records for this location yet.</p>
            </div>
            <div v-else class="inv-rt">
                <!-- Table Header -->
                <div class="inv-rt-head">
                    <div class="inv-rt-col">Date</div>
                    <div class="inv-rt-col">Source</div>
                    <div class="inv-rt-col">Location</div>
                    <div class="inv-rt-col">Status</div>
                    <div class="inv-rt-col is-num">Qty</div>
                </div>

                <!-- Table Rows -->
                <div v-for="h in received_list" :key="h.source_table + '-' + h.order_id + '-' + h.version" class="inv-rt-row">
                    <!-- Date -->
                    <div class="inv-rt-col inv-rt-date">
                        <div class="inv-rt-date-main">{{ format_date_short(h.order_date) }}</div>
                        <div class="inv-rt-date-sub">{{ format_time(h.order_date) }}</div>
                    </div>

                    <!-- Source -->
                    <div class="inv-rt-col inv-rt-source">
                        <span class="inv-rt-icon" :class="'is-' + recvSourceClass(h.order_type)">
                            <b-icon :icon="recvSourceIcon(h.order_type)"></b-icon>
                        </span>
                        <div>
                            <div class="inv-rt-title">{{ h.order_type }}</div>
                            <div class="inv-rt-sub">#{{ h.order_id }} · {{ h.created_by || 'System' }}</div>
                        </div>
                    </div>

                    <!-- Location -->
                    <div class="inv-rt-col inv-rt-loc">
                        <div v-if="h.drop_location" class="inv-rt-title">{{ locsubLabel(h.drop_location) }}</div>
                        <div v-if="h.drop_location" class="inv-rt-sub">{{ h.drop_location }}</div>
                        <div v-else class="inv-rt-sub is-muted">—</div>
                    </div>

                    <!-- Status -->
                    <div class="inv-rt-col">
                        <span class="inv-rt-status">
                            <b-icon icon="check-circle-fill"></b-icon>
                            {{ h.status_label || 'Received' }}
                        </span>
                    </div>

                    <!-- Qty -->
                    <div class="inv-rt-col is-num inv-rt-qty">
                        <span class="inv-rt-qty-val">+{{ h.quantity }}</span>
                    </div>
                </div>
            </div>
        </b-modal>

        <!-- IMAGE VIEWER MODAL -->
        <b-modal
            id="inv-image-viewer"
            v-model="fullImageVisible"
            size="lg"
            centered
            hide-footer
            :title="fullImageName || 'Product Image'"
            body-class="inv-img-modal-body"
        >
            <div class="text-center">
                <b-img
                    v-if="fullImageUrl"
                    :src="fullImageUrl"
                    fluid
                    style="max-height: 70vh; object-fit: contain;"
                ></b-img>
            </div>
        </b-modal>
    </div>
</template>

<script>
import moment from "moment";
import Requestor from "@/utilities/Requestor";

export default {
    name: "LookupInventory",
    data() {
        return {
            packet: {},
            product_query: null,
            message: "No item to show — search above.",
            items_being_loaded: false,
            items: [],
            activeSortKey: null,
            activeSortAsc: true,

            expandedLocations: {},
            lastFocused: null,

            receivedTotals: {},
            
            // Built dynamically from fetched items to fix the missing dict issue
            dynamicLocsubsdict: {},

            history: [],
            history_visible: false,
            history_loading: false,
            history_heading: null,

            received_list: [],
            received_visible: false,
            received_loading: false,
            received_heading: null,
            received_total: 0,

            fullImageVisible: false,
            fullImageUrl: null,
            fullImageName: null,
        };
    },
    computed: {
        isAdmin() {
            return this.packet.userDetails.userrole === 'admin';
        },
        totalUnits() {
            return this.items.reduce((s, d) => s + (Number(d.nitems) || 0), 0);
        },
        sortOptions() {
            return [
                { key: 'product_name', label: 'Product name' },
                { key: 'product_sku',  label: 'SKU' },
                { key: 'locsub',       label: 'Location' },
                { key: 'nitems',       label: 'Qty' },
                { key: 'reserved',     label: 'Reserved' },
                { key: 'available',    label: 'Available' },
                { key: 'missing',      label: 'Missing' },
                { key: 'received',     label: 'Received' },
            ];
        },
        groupedItems() {
            const locMap = new Map();
            for (const it of this.items) {
                const locKey = `${it.product_sku}__loc-${it.location_id}`;
                if (!locMap.has(locKey)) {
                    locMap.set(locKey, {
                        key: locKey,
                        location_id: it.location_id,
                        locationName: this.locationLabel(it.location_id, it.locsub),
                        representative: it,
                        subMap: new Map(),
                    });
                }
                const bucket = locMap.get(locKey);
                const subKey = it.locsub || `${it.location_id}|${it.sublocation_id}`;
                if (!bucket.subMap.has(subKey)) {
                    bucket.subMap.set(subKey, {
                        key: `${locKey}__${subKey}`,
                        locsub: it.locsub,
                        location_id: it.location_id,
                        sublocation_id: it.sublocation_id,
                        item: it,
                    });
                }
            }

            const out = [];
            for (const loc of locMap.values()) {
                const sublocations = [];
                for (const sub of loc.subMap.values()) {
                    sub.nitems    = Number(sub.item.nitems)    || 0;
                    sub.reserved  = Number(sub.item.reserved)  || 0;
                    sub.available = Number(sub.item.available) || 0;
                    sub.missing   = Number(sub.item.missing)   || 0;
                    sub.received  = this.receivedTotalFor(sub.item);
                    sub.is_out_of_stock = !!sub.item.is_out_of_stock;
                    sub.sublocationLabel = this.sublocationLabel(sub.locsub, sub.sublocation_id);
                    sub.stockState = this.stockState(sub.item);
                    sublocations.push(sub);
                }
                sublocations.sort((a, b) => String(a.locsub).localeCompare(String(b.locsub)));

                const nitems    = sublocations.reduce((s, x) => s + x.nitems, 0);
                const reserved  = sublocations.reduce((s, x) => s + x.reserved, 0);
                const available = sublocations.reduce((s, x) => s + x.available, 0);
                const missing   = sublocations.reduce((s, x) => s + x.missing, 0);
                const received  = sublocations.reduce((s, x) => s + x.received, 0);

                let stockState = 'ok';
                if (sublocations.every(x => x.is_out_of_stock)) stockState = 'out';
                else if (available > 0 && available <= 3) stockState = 'low';

                out.push({
                    key: loc.key,
                    location_id: loc.location_id,
                    locationName: loc.locationName,
                    representative: loc.representative,
                    sublocations,
                    nitems, reserved, available, missing, received,
                    stockState,
                    expanded: !!this.expandedLocations[loc.key],
                });
            }
            return out;
        },
        allExpanded() {
            return this.groupedItems.length > 0 && this.groupedItems.every(g => g.expanded);
        },
    },
    methods: {
        receivedTotalFor(item) {
            if (!item) return 0;
            const key = `${(item.product_sku || '').toUpperCase()}|${item.location_id}|${item.sublocation_id}`;
            return this.receivedTotals[key] || 0;
        },

        fetch_data(e) {
            if (e) e.preventDefault();
            if (!this.product_query || this.product_query.length <= 1) {
                this.$bvToast.toast(
                    "Please type a more specific name OR SKU greater than 1 character.",
                    { title: "Validation", autoHideDelay: 2200, variant: "warning" }
                );
                return;
            }
            const path = "/api/inventory/loc_sub_product_inventory/";
            const data = { product_query: this.product_query };
            if (this.packet.userDetails.properties.location_id) {
                data.location_id = this.packet.userDetails.properties.location_id;
            }
            const that = this;
            const s = (response) => {
                if (response.data.result) {
                    that.items = response.data.result;
                    that.expandedLocations = {};
                    that.receivedTotals = {};
                    
                    // Build dynamic dict for location names based on fetched items
                    const dict = {};
                    that.items.forEach(i => {
                        if (i.locsub && !dict[i.locsub]) {
                            // We only have the raw ID strings here, but if your API ever sends a friendly name
                            // we can map it here. Otherwise, `locsubLabel` fallback handles it.
                            dict[i.locsub] = i.locsub; 
                        }
                    });
                    that.dynamicLocsubsdict = dict;

                    const locs = new Set(that.items.map(i => `${i.product_sku}__loc-${i.location_id}`));
                    if (locs.size === 1) {
                        const k = Array.from(locs)[0];
                        that.$set(that.expandedLocations, k, true);
                    }
                    that.fetch_received_totals(that.items);
                } else {
                    that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1800, variant: "warning" });
                }
                that.items_being_loaded = false;
            };
            const f = (error) => {
                console.error(error);
                that.$bvToast.toast("Error loading inventory.", { title: "Error", autoHideDelay: 1800, variant: "warning" });
                that.items_being_loaded = false;
            };
            this.items_being_loaded = true;
            Requestor.postSyncP(path, data, this, s, f);
        },

        fetch_received_totals(items) {
            if (!items || items.length === 0) return;
            const rows = items.map(i => ({
                product_sku:    i.product_sku,
                location_id:    i.location_id,
                sublocation_id: i.sublocation_id,
            }));
            const path = "/api/inventory/get_received_totals_for_rows/";
            const that = this;
            const s = (response) => {
                if (response.data && response.data.result) {
                    that.receivedTotals = response.data.result;
                }
            };
            const f = (error) => {
                console.error("Received totals fetch failed:", error);
            };
            Requestor.postSyncP(path, { rows }, this, s, f);
        },

        update_inventory(item) {
            if (isNaN(parseInt(item.nitems))) {
                this.$bvToast.toast("Please specify a valid quantity >= 0.", { title: "Validation", autoHideDelay: 2000, variant: "warning" });
                return;
            }
            item.nitems = parseInt(item.nitems);

            const path = "/api/inventory/loc_sub_product_inventory_update/";
            const data = Object.assign({}, item, {
                user_id:   this.packet.userDetails.user_id,
                user_name: this.packet.userDetails.username || this.packet.userDetails.name || this.packet.userDetails.email,
                note:      null,
            });
            delete data.image_url;
            delete data.barcode_image;
            delete data._imgError;

            const that = this;
            const s = (response) => {
                if (response.data.result) {
                    that.$bvToast.toast("Updated.", { title: "Success", autoHideDelay: 1500, variant: "success" });
                    const r = response.data.result[0] || {};
                    if (r.missing_after !== undefined) item.missing = r.missing_after;
                    if (r.new_nitems !== undefined) {
                        item.nitems = r.new_nitems;
                        item.available = r.new_nitems - (item.reserved || 0);
                    }
                } else {
                    that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 2000, variant: "warning" });
                }
            };
            const f = (error) => {
                console.error(error);
                that.$bvToast.toast("Error updating inventory.", { title: "Error", autoHideDelay: 2000, variant: "danger" });
            };
            Requestor.postSyncP(path, data, this, s, f);
        },

        toggleLocation(loc) {
            this.$set(this.expandedLocations, loc.key, !loc.expanded);
        },
        toggleExpandAll() {
            const target = !this.allExpanded;
            for (const g of this.groupedItems) {
                this.$set(this.expandedLocations, g.key, target);
            }
        },

        show_history(item) {
            this.history = [];
            this.history_heading = {
                product_sku: item.product_sku,
                locsub: item.locsub || `${item.location_id} · ${item.sublocation_id}`
            };
            this.history_visible = true;
            this.history_loading = true;

            const path = "/api/inventory/loc_sub_product_inventory_history/";
            const data = {
                product_sku:    item.product_sku,
                location_id:    item.location_id,
                sublocation_id: item.sublocation_id,
            };
            const that = this;
            const s = (response) => {
                that.history = response.data.result || [];
                that.history_loading = false;
            };
            const f = (error) => {
                console.error(error);
                that.$bvToast.toast("Error loading history.", { title: "Error", autoHideDelay: 1800, variant: "warning" });
                that.history_loading = false;
            };
            Requestor.postSyncP(path, data, this, s, f);
        },

        show_received_history(item) {
            this.received_list = [];
            this.received_heading = {
                product_sku: item.product_sku,
                locsub: item.locsub || `${item.location_id} · ${item.sublocation_id}`
            };
            this.received_total = this.receivedTotalFor(item);
            this.received_visible = true;
            this.received_loading = true;

            const path = "/api/product/get_received_history_scoped/";
            const data = {
                product_sku:    item.product_sku,
                location_id:    item.location_id,
                sublocation_id: item.sublocation_id,
            };
            const that = this;
            const s = (response) => {
                that.received_list = response.data.result || [];
                that.received_loading = false;
            };
            const f = (error) => {
                console.error(error);
                that.$bvToast.toast("Error loading received history.", { title: "Error", autoHideDelay: 1800, variant: "warning" });
                that.received_loading = false;
            };
            Requestor.postSyncP(path, data, this, s, f);
        },

        format_date(v) {
            return v ? moment(v).format("MMM DD, YYYY · HH:mm") : "";
        },
        format_date_short(v) {
            return v ? moment(v).format("MMM DD, YYYY") : "—";
        },
        format_time(v) {
            return v ? moment(v).format("HH:mm") : "";
        },

        recvSourceIcon(order_type) {
            const t = (order_type || '').toLowerCase();
            if (t.includes('container')) return 'box-seam';
            if (t.includes('backorder')) return 'arrow-repeat';
            return 'cart';
        },
        recvSourceClass(order_type) {
            const t = (order_type || '').toLowerCase();
            if (t.includes('container')) return 'container';
            if (t.includes('backorder')) return 'backorder';
            return 'stock';
        },

        productImage(item) {
            if (!item || !item.image_url) return null;
            let url = String(item.image_url).trim();
            if (!url || url === 'null' || url === 'undefined') return null;
            if (url.includes('fmapi.myfurnituremecca.com')) {
                return `/api/image/image-proxy/?url=${encodeURIComponent(url)}`;
            }
            if (!/^https?:\/\//i.test(url) && !url.startsWith('/')) url = '/' + url;
            return url;
        },
        showFullImage(item) {
            if (!item) return;
            const url = this.productImage(item);
            if (!url) return;
            this.fullImageUrl = url;
            this.fullImageName = this.trimName(item.product_name) || item.product_sku;
            this.fullImageVisible = true;
        },

        sortby(key) {
            const asc = this.activeSortKey === key ? !this.activeSortAsc : true;
            const sorted = [...this.items].sort((a, b) => {
                const av = a[key] || '';
                const bv = b[key] || '';
                if (typeof av === 'number' && typeof bv === 'number') return asc ? av - bv : bv - av;
                return asc
                    ? String(av).localeCompare(String(bv))
                    : String(bv).localeCompare(String(av));
            });
            this.items = sorted;
            this.activeSortKey = key;
            this.activeSortAsc = asc;
        },
        onSortSelect(key) {
            if (!key) return;
            this.activeSortKey = null;
            this.sortby(key);
        },
        pcSortClass(key) {
            return ['pc-head-sort', { 'is-active': this.activeSortKey === key }];
        },

        locsubLabel(locsub) {
            if (!locsub) return 'N/A';
            // Prioritize the packet dict, fallback to raw string
            return this.packet.locsubsdict[locsub] || locsub;
        },
        locationLabel(location_id, locsub) {
            const dict = this.packet.locationsdict;
            if (dict && dict[location_id]) return dict[location_id];
            const label = this.locsubLabel(locsub);
            if (label && label.includes('|')) return label.split('|')[0].trim();
            return `Location ${location_id}`;
        },
        sublocationLabel(locsub, sublocation_id) {
            const label = this.locsubLabel(locsub);
            if (label && label.includes('|')) return label.split('|')[1].trim();
            return `Sublocation ${sublocation_id}`;
        },
        trimName(name) { return (name || '').trim(); },
        isLow(item) {
            const available = Number(item.available) || 0;
            return available > 0 && available <= 3;
        },
        stockState(item) {
            if (item.is_out_of_stock) return 'out';
            if (this.isLow(item)) return 'low';
            return 'ok';
        },
        total(key) {
            if (key === 'received') {
                return Object.values(this.receivedTotals).reduce((s, v) => s + (Number(v) || 0), 0);
            }
            return this.items.reduce((sum, d) => sum + (Number(d[key]) || 0), 0);
        },
        isEditing(item) { return this.lastFocused === item; },
        onQtyFocus(item) { this.lastFocused = item; },
        onQtyBlur(item) { if (!this.isAdmin) this.lastFocused = null; },
    },
    created() {
        this.packet = JSON.parse(localStorage.packet);
        this.packet.userDetails = JSON.parse(localStorage.userDetails);
    },
};
</script>

<!-- =============================================================
     SCOPED STYLES
     ============================================================= -->
<style scoped>
.inv-page { max-width: 1100px; width: 96%; margin: 12px auto 24px; }

.inv-toolbar {
    display: flex; align-items: center; justify-content: space-between;
    gap: 12px; flex-wrap: wrap;
    background: var(--pl-ink) !important; border-radius: 16px !important;
    padding: 10px 12px !important; margin-bottom: 14px !important;
}
.inv-toolbar-label { display: inline-flex; align-items: center; gap: 8px; color: #fff; font-family: 'Sora', sans-serif; font-weight: 700; font-size: 14px; padding: 0 6px; }
.inv-toolbar-label .pc-ico { color: #FFB06B; }
.inv-search-group {
    display: flex; align-items: center; gap: 6px;
    background: rgba(255,255,255,0.06) !important;
    border: 1px solid rgba(255,255,255,0.14) !important;
    border-radius: 12px !important; padding: 3px !important;
    flex: 1 1 420px; max-width: 640px; margin-left: auto;
}
.inv-search-input { flex: 1; background: transparent !important; border: 0 !important; color: #fff !important; font-size: 14px; font-weight: 600; padding: 6px 10px; }
.inv-search-input::placeholder { color: rgba(255,255,255,0.5); }
.inv-search-btn { background: var(--pl-orange) !important; border: 0 !important; color: #fff !important; font-weight: 800; letter-spacing: 0.06em; font-size: 12px; padding: 6px 16px !important; border-radius: 9px !important; }
.inv-search-btn:hover:not(:disabled) { background: var(--pl-orange-dark) !important; }

.inv-list {
    --pc-ink: #221F1C; --pc-ink-2: #34302B;
    --pc-orange: #EE7A22; --pc-orange-dark: #D4620F; --pc-orange-soft: #FFF1E4;
    --pc-teal: #0F6E6B; --pc-teal-soft: #E2F2F0;
    --pc-red: #C93C30; --pc-red-soft: #FBE9E7;
    --pc-warn: #9A6A0C; --pc-warn-soft: #FDF3DE;
    --pc-panel: #F7F5F2; --pc-line: #E8E3DB; --pc-muted: #736C63;
    --pc-head: 'Sora', 'Manrope', system-ui, sans-serif;
    font-family: 'Manrope', system-ui, -apple-system, sans-serif;
    color: var(--pc-ink); background: transparent; border: 0 !important; height: auto; padding: 0;
}
.pc-ico { position: relative !important; display: inline-block !important; width: 1em !important; height: 1em !important; flex-shrink: 0; vertical-align: -0.125em; line-height: 1; }
.pc-ico >>> svg { position: absolute !important; top: 50% !important; left: 50% !important; width: 1em !important; height: 1em !important; margin: 0 !important; transform: translate(-50%, -50%) !important; display: block !important; }
.inv-list >>> svg.b-icon { width: 1em; height: 1em; vertical-align: -0.125em; }

.pc-bar { position: sticky; top: 0; z-index: 7; display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; padding: 8px 10px; margin-bottom: 8px; background: rgba(255,255,255,0.97); border: 1px solid var(--pc-line); border-radius: 14px; }
.pc-bar-sort, .pc-bar-view { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.pc-bar-label { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 700; color: var(--pc-muted); }
.pc-bar-label .pc-ico { color: var(--pc-orange); }
.pc-bar-select { height: 36px; min-width: 190px; padding: 0 30px 0 12px; border: 1.5px solid var(--pc-line); border-radius: 10px; background-color: var(--pc-panel); font-size: 14px; font-weight: 700; color: var(--pc-ink); }
.pc-bar-btn { display: inline-flex; align-items: center; gap: 6px; height: 36px; padding: 0 12px; border: 1.5px solid var(--pc-line); border-radius: 10px; background: #fff; font-size: 13px; font-weight: 700; color: var(--pc-ink); cursor: pointer; }
.pc-bar-btn:hover { border-color: var(--pc-orange); color: var(--pc-orange-dark); }
.pc-bar-count { font-size: 13px; font-weight: 700; color: var(--pc-muted); }

.inv-head,
.inv-totals,
.inv-sum {
    display: grid;
    grid-template-columns:
        200px
        minmax(0, 1.4fr)
        minmax(0, 0.6fr)
        50px 50px 50px 50px 50px
        50px;
    align-items: center;
    column-gap: 14px;
}

.pc-head { padding: 0 18px; margin-bottom: 6px; height: 38px; background: var(--pc-ink); border-radius: 12px; }
.pc-head-cell { display: flex; align-items: center; gap: 10px; min-width: 0; }
.pc-head-cell.is-num { justify-content: flex-end; }
.pc-head-sort { display: inline-flex; align-items: center; gap: 4px; padding: 0; border: 0; background: transparent; color: rgba(255,255,255,0.72); font-size: 11.5px; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; cursor: pointer; white-space: nowrap; }
.pc-head-sort:hover { color: #fff; }
.pc-head-sort.is-active { color: #FFB06B; }

.inv-totals { padding: 10px 18px; margin-bottom: 10px; background: #FFFBEB; border: 1px solid #FDE68A; border-radius: 12px; font-weight: 800; }
.inv-total-val { font-family: var(--pc-head); font-size: 15px; color: var(--pc-ink); }
.inv-total-val.is-good { color: var(--pc-teal); }
.inv-total-val.is-bad { color: var(--pc-red); }

.inv-card { margin-bottom: 10px; }
.pc-card { position: relative; background: #fff; border: 1px solid var(--pc-line); border-radius: 14px; transition: border-color 0.15s ease, box-shadow 0.15s ease; }
.pc-card::before { content: ''; position: absolute; left: -1px; top: 14px; width: 4px; height: 56px; border-radius: 0 4px 4px 0; background: var(--pc-teal); }
.pc-card.is-low::before { background: #E0A21B; }
.pc-card.is-out::before { background: var(--pc-red); }
.pc-card:hover { border-color: #D9D1C5; box-shadow: 0 8px 22px -16px rgba(34, 31, 28, 0.4); }
.pc-card.is-open { border-color: var(--pc-orange); box-shadow: 0 0 0 3px rgba(238, 122, 34, 0.12); }

.inv-sum { padding: 14px 16px 14px 20px; }
.inv-col { min-width: 0; }

.inv-col-img { width: 200px; height: 150px; border-radius: 10px; background: linear-gradient(160deg, #F7F3EE, #EDE8E1); overflow: hidden; position: relative; flex-shrink: 0; }
.inv-img-btn { position: absolute; inset: 0; padding: 4px; border: 0; background: transparent; cursor: zoom-in; }
.inv-img-btn img { width: 100%; height: 100%; object-fit: contain; display: block; }
.inv-img-empty { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; color: #B9B1A6; font-size: 22px; }

.inv-col-product { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.inv-name { font-family: var(--pc-head); font-size: 15px; font-weight: 700; line-height: 1.3; color: var(--pc-ink); display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.inv-sku-row { display: flex; align-items: baseline; gap: 6px; }
.inv-sku-label { font-size: 10.5px; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; color: var(--pc-muted); }
.inv-sku { font-family: 'Menlo', 'Consolas', monospace; font-size: 13px; font-weight: 700; color: var(--pc-ink-2); word-break: break-all; }
.inv-badges { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 2px; }
.inv-badges:empty { display: none; }

.inv-loc-name { font-size: 14px; font-weight: 700; color: var(--pc-ink); margin-bottom: 2px; }
.inv-loc-meta { display: inline-flex; align-items: center; gap: 5px; font-size: 12px; font-weight: 600; color: var(--pc-muted); }
.inv-loc-meta .pc-ico { color: var(--pc-orange); }

.inv-col-num { text-align: right; }
.inv-v { font-family: var(--pc-head); font-size: 16px; font-weight: 800; font-variant-numeric: tabular-nums; color: var(--pc-ink); }
.inv-v.is-reserved { color: #D4620F; }
.inv-v.is-good { color: var(--pc-teal); }
.inv-v.is-bad { color: var(--pc-red); }
.inv-v.is-muted { color: var(--pc-muted); }
.inv-v.is-received { color: #1F6690; }

.inv-col-actions { display: flex; align-items: center; justify-content: flex-end; }
.pc-icon-btn { position: relative; width: 34px; height: 34px; padding: 0 !important; border: 1px solid var(--pc-line) !important; border-radius: 9px !important; background: #fff !important; color: var(--pc-ink-2) !important; font-size: 15px; cursor: pointer; }
.pc-icon-btn > .pc-ico { position: absolute !important; top: 50% !important; left: 50% !important; transform: translate(-50%, -50%); }
.pc-icon-btn:hover { border-color: var(--pc-orange) !important; color: var(--pc-orange-dark) !important; }
.pc-icon-btn.is-received { background: #EEF5FB !important; border-color: #C3DCEC !important; color: #1F6690 !important; }
.pc-icon-btn.is-received:hover { background: #D6E9F5 !important; }
.inv-location-card.is-open .inv-expand-btn { background: var(--pc-ink) !important; border-color: var(--pc-ink) !important; color: #fff !important; }

.inv-sublocations { border-top: 1px dashed var(--pc-line); background: #FCFBF9; border-radius: 0 0 14px 14px; padding: 8px 14px 14px; }

.inv-subloc-head-row,
.inv-subloc-row {
    display: grid;
    grid-template-columns: minmax(0, 1.8fr) 100px 100px 100px 100px 100px 200px;
    align-items: center;
    column-gap: 12px;
    padding: 10px 20px;
}

.inv-subloc-head-row { padding: 6px 20px 8px; border-bottom: 1px solid var(--pc-line); margin-bottom: 8px; }
.inv-sub-col { font-size: 10.5px; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; color: var(--pc-muted); }
.inv-sub-col.is-num { text-align: right; }

.inv-subloc-row { background: #fff; border: 1px solid #EDE8E1; border-radius: 10px; margin-bottom: 8px; position: relative; transition: border-color 0.12s ease, box-shadow 0.12s ease; }
.inv-subloc-row:last-child { margin-bottom: 0; }
.inv-subloc-row::before { content: ''; position: absolute; left: -1px; top: 12px; width: 3px; height: 36px; border-radius: 0 3px 3px 0; background: var(--pc-teal); }
.inv-subloc-row.is-low::before { background: #E0A21B; }
.inv-subloc-row.is-out::before { background: var(--pc-red); }
.inv-subloc-row:hover { border-color: #D9D1C5; }
.inv-subloc-row.is-editing { border-color: var(--pc-orange); box-shadow: 0 0 0 3px rgba(238, 122, 34, 0.12); }

.inv-sub-col-name { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.inv-subloc-title { font-family: var(--pc-head); font-size: 14px; font-weight: 800; color: var(--pc-ink); }
.inv-subloc-meta { display: inline-flex; align-items: center; flex-wrap: wrap; gap: 6px; }
.inv-subloc-chip { font-family: 'Menlo', 'Consolas', monospace; font-size: 11px; font-weight: 700; color: var(--pc-orange-dark); background: var(--pc-orange-soft); padding: 1px 8px; border-radius: 99px; }

.inv-sub-col-qty { display: flex; justify-content: flex-end; }
.inv-qty-input { width: 90px; text-align: center; }
.pc-input.form-control { height: 36px; border: 1.5px solid #DCD5CB; border-radius: 8px; font-size: 14px; font-weight: 800; color: var(--pc-ink); background-color: #fff; font-variant-numeric: tabular-nums; }
.pc-input.form-control:focus { border-color: var(--pc-orange); box-shadow: 0 0 0 3px rgba(238,122,34,0.18); }
.pc-input.form-control:disabled { background-color: #F7F5F2; color: var(--pc-muted); }

.inv-sub-col-num { text-align: right; }

.inv-sub-col-actions { display: flex; align-items: center; gap: 6px; justify-content: flex-end; }
.pc-btn { display: inline-flex; align-items: center; gap: 6px; height: 34px; padding: 0 12px; border: 1px solid var(--pc-line); border-radius: 9px; background: #fff; font-size: 12.5px; font-weight: 800; color: var(--pc-ink); cursor: pointer; white-space: nowrap; }
.pc-btn.is-primary { background: var(--pc-orange); border-color: var(--pc-orange); color: #fff; }
.pc-btn.is-primary:hover:not(:disabled) { background: var(--pc-orange-dark); }
.pc-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.pc-badge { display: inline-block; padding: 2px 9px; border-radius: 99px; font-size: 11px; font-weight: 800; line-height: 1.4; background: #fff; border: 1px solid var(--pc-line); color: var(--pc-ink); white-space: nowrap; }
.pc-badge.is-good { background: var(--pc-teal-soft); border-color: #BFE1DD; color: var(--pc-teal); }
.pc-badge.is-danger { background: var(--pc-red-soft); border-color: #F3C9C3; color: var(--pc-red); }
.pc-badge.is-info { background: #E2EFF7; border-color: #C3DCEC; color: #1F6690; }
.pc-badge.is-warn { background: var(--pc-warn-soft); border-color: #F1DDAE; color: var(--pc-warn); }
.pc-badge.is-muted { background: #EFECE7; color: var(--pc-muted); }
.pc-badge.is-received { background: #E2EFF7; border-color: #C3DCEC; color: #1F6690; }

.inv-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px; padding: 60px 20px; background: #fff; border: 1px dashed var(--pl-line); border-radius: 14px; color: var(--pl-muted); font-weight: 700; }
.inv-empty p { margin: 0; }
.inv-empty-ico { font-size: 40px; color: var(--pl-orange); opacity: 0.7; }

/* ================================================
   MODAL STYLING
   ================================================ */
.inv-modal-header { background: var(--pl-ink) !important; color: #fff !important; border: 0 !important; padding: 14px 20px !important; box-shadow: inset 0 -3px 0 var(--pl-orange); }
.inv-modal-header--received { box-shadow: inset 0 -3px 0 #1F6690; }
.inv-modal-header .modal-title { font-family: 'Sora', sans-serif !important; font-weight: 700 !important; font-size: 16px !important; }
.inv-modal-header .close { color: #fff !important; opacity: 0.85 !important; text-shadow: none !important; }
.inv-modal-body { background: #F7F5F2; padding: 16px; }

/* Summary header inside modals */
.inv-modal-summary {
    display: flex;
    align-items: center;
    gap: 12px;
    background: #fff;
    border: 1px solid var(--pl-line);
    border-radius: 12px;
    padding: 12px 14px;
    margin-bottom: 16px;
}
.inv-modal-summary-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    flex-shrink: 0;
}
.inv-modal-summary-icon.is-orange { background: var(--pl-orange-soft); color: var(--pl-orange-dark); }
.inv-modal-summary-icon.is-blue { background: #E2EFF7; color: #1F6690; }
.inv-modal-summary-text { flex: 1 1 auto; min-width: 0; }
.inv-modal-summary-title { font-family: 'Sora', sans-serif; font-size: 15px; font-weight: 800; color: var(--pl-ink); word-break: break-all; }
.inv-modal-summary-sub { display: inline-flex; align-items: center; gap: 5px; font-size: 12.5px; font-weight: 700; color: var(--pl-muted); margin-top: 2px; }
.inv-modal-summary-sub .pc-ico { color: var(--pl-orange); }
.inv-modal-summary-count { flex-shrink: 0; text-align: right; padding-left: 14px; border-left: 1px dashed var(--pl-line); }
.inv-modal-summary-count b { display: block; font-family: 'Sora', sans-serif; font-size: 20px; font-weight: 800; color: var(--pl-orange-dark); line-height: 1; }
.inv-modal-summary-count.is-blue b { color: #1F6690; }
.inv-modal-summary-count small { display: block; font-size: 10.5px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: var(--pl-muted); margin-top: 4px; }

/* ============ UPDATE HISTORY TIMELINE ============ */
.inv-timeline { display: flex; flex-direction: column; gap: 10px; }
.inv-timeline-item { position: relative; display: flex; gap: 12px; }
.inv-timeline-marker {
    flex-shrink: 0;
    width: 28px; height: 28px;
    border-radius: 50%;
    background: var(--pl-orange-soft);
    color: var(--pl-orange-dark);
    display: flex; align-items: center; justify-content: center;
    font-size: 12px;
    border: 2px solid #fff;
    box-shadow: 0 0 0 1px var(--pl-line);
    margin-top: 10px;
}
.inv-timeline-card {
    flex: 1 1 auto;
    min-width: 0;
    background: #fff;
    border: 1px solid var(--pl-line);
    border-radius: 12px;
    padding: 10px 14px;
}
.inv-timeline-head {
    display: flex; align-items: center; justify-content: space-between;
    gap: 10px; flex-wrap: wrap;
    padding-bottom: 8px;
    border-bottom: 1px dashed var(--pl-line);
    margin-bottom: 8px;
}
.inv-timeline-when { font-size: 12px; font-weight: 700; color: var(--pl-muted); font-variant-numeric: tabular-nums; }
.inv-timeline-user { display: inline-flex; align-items: center; gap: 4px; font-size: 12px; font-weight: 700; color: var(--pl-orange-dark); }
.inv-timeline-body { display: flex; flex-direction: column; gap: 6px; }
.inv-timeline-qty { display: inline-flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.inv-qty-old { color: var(--pl-muted); text-decoration: line-through; font-family: 'Sora', sans-serif; font-size: 15px; }
.inv-qty-arrow { color: var(--pl-muted); font-weight: 800; }
.inv-qty-new { font-family: 'Sora', sans-serif; font-size: 18px; font-weight: 800; color: var(--pl-ink); }
.inv-qty-delta { padding: 2px 9px; border-radius: 99px; font-family: 'Sora', sans-serif; font-size: 12px; font-weight: 800; }
.inv-qty-delta.is-pos { background: var(--pl-teal-soft); color: var(--pl-teal); }
.inv-qty-delta.is-neg { background: var(--pl-red-soft); color: var(--pl-red); }
.inv-timeline-missing { display: inline-flex; align-items: center; gap: 6px; padding: 6px 10px; border-radius: 8px; background: var(--pl-amber-soft); color: var(--pl-amber); font-size: 12px; font-weight: 700; }
.inv-timeline-missing b { color: var(--pl-amber); }
.inv-timeline-note { display: flex; align-items: flex-start; gap: 6px; padding: 6px 10px; border-radius: 8px; background: var(--pc-panel); color: var(--pc-muted); font-size: 12px; font-weight: 600; font-style: italic; }
.inv-muted { color: var(--pl-muted); font-weight: 600; }

/* ============ RECEIVED HISTORY TABLE ============ */
.inv-rt {
    background: #fff;
    border: 1px solid var(--pl-line);
    border-radius: 12px;
    overflow: hidden;
}
.inv-rt-head {
    display: grid;
    grid-template-columns: 120px minmax(0, 1.4fr) minmax(0, 1.2fr) 100px 70px;
    gap: 12px;
    padding: 10px 16px;
    background: var(--pc-panel);
    border-bottom: 1px solid var(--pl-line);
    font-size: 10.5px;
    font-weight: 800;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--pl-muted);
}
.inv-rt-row {
    display: grid;
    grid-template-columns: 120px minmax(0, 1.4fr) minmax(0, 1.2fr) 100px 70px;
    gap: 12px;
    align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid var(--pl-line);
    transition: background 0.15s ease;
}
.inv-rt-row:last-child { border-bottom: 0; }
.inv-rt-row:hover { background: #FAF9F7; }
.inv-rt-col { min-width: 0; }
.inv-rt-col.is-num { text-align: right; }

.inv-rt-date-main { font-family: var(--pc-head); font-size: 13px; font-weight: 800; color: var(--pl-ink); }
.inv-rt-date-sub { font-size: 11.5px; font-weight: 600; color: var(--pl-muted); margin-top: 1px; font-variant-numeric: tabular-nums; }

.inv-rt-source { display: flex; align-items: center; gap: 10px; }
.inv-rt-icon {
    width: 32px; height: 32px;
    border-radius: 8px;
    display: flex; align-items: center; justify-content: center;
    font-size: 14px;
    flex-shrink: 0;
}
.inv-rt-icon.is-stock { background: #E2EFF7; color: #1F6690; }
.inv-rt-icon.is-container { background: var(--pl-teal-soft); color: var(--pl-teal); }
.inv-rt-icon.is-backorder { background: var(--pl-amber-soft); color: var(--pl-amber); }

.inv-rt-title { font-size: 13px; font-weight: 800; color: var(--pl-ink); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.inv-rt-sub { font-size: 11.5px; font-weight: 600; color: var(--pl-muted); margin-top: 1px; }
.inv-rt-sub.is-muted { color: #A59D92; }

.inv-rt-status { display: inline-flex; align-items: center; gap: 5px; font-size: 11.5px; font-weight: 800; color: var(--pl-teal); background: var(--pl-teal-soft); padding: 3px 8px; border-radius: 99px; }
.inv-rt-qty-val { font-family: var(--pc-head); font-size: 16px; font-weight: 800; color: #1F6690; font-variant-numeric: tabular-nums; }

.inv-img-modal-body { background: #F7F5F2; }

/* ---------- responsive ---------- */
@media (max-width: 1199.98px) {
    .inv-head { display: none; }
    .inv-totals { display: none; }

    .inv-sum {
        grid-template-columns: 90px minmax(0, 1fr) auto auto auto auto auto;
        grid-template-areas:
            "img product loc loc loc act act"
            "img nums    nums nums nums nums nums";
        row-gap: 10px;
        padding: 14px;
    }
    .inv-col-img     { grid-area: img; width: 90px; height: 90px; }
    .inv-col-product { grid-area: product; }
    .inv-col-loc     { grid-area: loc; }
    .inv-col-num {
        grid-area: nums; display: grid; grid-template-columns: repeat(5, 1fr);
        gap: 6px; text-align: center;
    }
    .inv-col-num::before {
        content: ''; display: block; font-size: 10px; font-weight: 800;
        letter-spacing: 0.04em; color: var(--pc-muted); text-transform: uppercase; margin-bottom: 2px;
    }
    .inv-col-num:nth-of-type(1)::before { content: 'QTY'; }
    .inv-col-num:nth-of-type(2)::before { content: 'RESERVED'; }
    .inv-col-num:nth-of-type(3)::before { content: 'AVAILABLE'; }
    .inv-col-num:nth-of-type(4)::before { content: 'MISSING'; }
    .inv-col-num:nth-of-type(5)::before { content: 'RECEIVED'; }
    .inv-col-actions { grid-area: act; justify-content: flex-end; align-self: start; }

    .inv-subloc-head-row { display: none; }
    .inv-subloc-row {
        grid-template-columns: minmax(0, 1fr) 90px 90px 90px 90px 90px;
        padding: 10px 12px;
    }
    .inv-sub-col-actions { grid-column: 1 / -1; justify-content: flex-end; margin-top: 6px; padding-top: 8px; border-top: 1px dashed #EDE8E1; }
}

@media (max-width: 767.98px) {
    .inv-page { width: 100%; padding: 0 8px; }
    .inv-toolbar { padding: 8px !important; }
    .inv-search-group { flex: 1 1 100%; max-width: none; margin-left: 0; }

    .inv-sum {
        grid-template-columns: 80px 1fr auto;
        grid-template-areas:
            "img  product  act"
            "loc  loc      loc"
            "nums nums     nums";
        row-gap: 10px;
        padding: 12px;
    }
    .inv-col-img     { grid-area: img; width: 80px; height: 80px; }
    .inv-col-product { grid-area: product; }
    .inv-col-loc     { grid-area: loc; }
    .inv-col-num {
        grid-area: nums; display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px;
    }
    .inv-col-num > * {
        display: block; padding: 6px 8px; background: var(--pc-panel);
        border-radius: 8px; text-align: center;
    }
    .inv-col-actions { grid-area: act; }

    .inv-sublocations { padding: 8px; }
    .inv-subloc-row { grid-template-columns: 1fr 1fr; row-gap: 8px; padding: 10px; }
    .inv-sub-col-name { grid-column: 1 / -1; padding-bottom: 6px; border-bottom: 1px dashed #EDE8E1; }
    .inv-sub-col-qty { grid-column: 1 / -1; justify-content: space-between; align-items: center; }
    .inv-sub-col-qty::before { content: 'QTY'; font-size: 10px; font-weight: 800; letter-spacing: 0.05em; color: var(--pc-muted); }
    .inv-qty-input { width: 100px; }
    .inv-sub-col-num { text-align: center; }
    .inv-sub-col-actions { grid-column: 1 / -1; justify-content: stretch; }
    .inv-sub-col-actions .pc-btn { flex: 1; justify-content: center; }

    .inv-modal-body { padding: 12px; }

    .inv-rt-head { display: none; }
    .inv-rt-row {
        grid-template-columns: 1fr auto;
        gap: 8px;
        padding: 14px 16px;
        position: relative;
    }
    .inv-rt-date {
        grid-column: 1 / -1;
        display: flex;
        gap: 6px;
        align-items: center;
        margin-bottom: 4px;
    }
    .inv-rt-date-main { font-size: 11.5px; font-weight: 700; color: var(--pl-muted); }
    .inv-rt-date-sub { font-size: 11.5px; margin-top: 0; }
    
    .inv-rt-source { grid-column: 1 / 2; }
    .inv-rt-loc { grid-column: 1 / 2; margin-top: 4px; padding-left: 42px; }
    
    .inv-rt-qty { grid-column: 2 / 3; grid-row: 2 / 3; }
    
    .inv-rt-row > .inv-rt-col:nth-child(4) {
        position: absolute;
        top: 14px; right: 16px;
    }
}

@media (max-width: 399.98px) {
    .inv-col-img { width: 64px; height: 64px; }
    .inv-col-num { grid-template-columns: repeat(2, 1fr); }
    .inv-subloc-row { grid-template-columns: 1fr; }
}
</style>

<!-- =============================================================
     UNSCOPED STYLES
     ============================================================= -->
<style>
@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=Sora:wght@600;700;800&display=swap');

.pl-page, .pl-modal {
  --pl-ink: #221F1C; --pl-ink-2: #34302B;
  --pl-orange: #EE7A22; --pl-orange-dark: #D4620F; --pl-orange-soft: #FFF1E4;
  --pl-teal: #0F6E6B; --pl-teal-soft: #E2F2F0;
  --pl-red: #C93C30; --pl-red-soft: #FBE9E7;
  --pl-amber: #C98A12; --pl-amber-soft: #FDF3DE;
  --pl-line: #E5E1DA; --pl-mist: #F6F4F0; --pl-muted: #736C63;
  font-family: 'Manrope', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  color: var(--pl-ink);
}
@media (pointer: coarse) {
  .pl-page .pc-icon-btn { width: 42px !important; height: 40px !important; }
  .pl-page .pc-btn { height: 40px; }
  .pl-page .pc-input.form-control { height: 44px; font-size: 16px; }
}
</style>


// WEBPACK FOOTER //
// src/components/Inventory/LookupInventory.vue